import React, { useContext, useEffect, useState } from 'react';
import { Container, Table, Button, Row, Col, Input, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label } from 'reactstrap';
import PaginationMaterial from "../../components/PaginationMaterial/PaginationMaterial";
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import Header from '../../components/Headers/Header';
import AdminNavbar from '../../components/Navbars/AdminNavbar/AdminNavbar';
import './UserType.css';
import { AuthContext } from '../../context/AuthContext';
import { UserTypeContext } from '../../context/UserTypeContext';
import { UserTypeType, CreateUserTypeType, UpdateUserTypeType } from '../../context/hooks/useUserType';
import useUser from '../../context/hooks/useUser';
import { PermissionModule, MODULE_LABELS, ModulePermissionType, createPermissionChecker } from '../../types/permissions';

const UserType = () => {
    const { objUserTk } = useContext(AuthContext);
    const { listUserType, pageCount, getAllUserTypePaged, createUserType, updateUserType, updateModulePermissions, deleteUserType } = useContext(UserTypeContext);
    const { getPermissionsByUser, userPermissions, useLegacyPermissions, modulePermissions } = useUser();

    const [selectedUserTypeId, setSelectedUserTypeId] = useState(0);
    const [newUserType, setNewUserType] = useState<CreateUserTypeType>({
        type: '',
        empresaId: objUserTk.EmpresaId,
        canViewAllSales: false,
        permissions: [],
    });

    const [permissions, setPermissions] = useState<number[]>([]);
    const [editingPermissions, setEditingPermissions] = useState<number[]>([]);
    const [editingModulePermissions, setEditingModulePermissions] = useState<ModulePermissionType[]>([]);

    const permissionOptions = [
        { label: "Adicionar", value: 1 },
        { label: "Visualizar", value: 2 },
        { label: "Atualizar", value: 3 },
        { label: "Deletar", value: 4 },
    ];

    const getPermissionColor = (permission: any) => {
        switch (permission) {
            case 1:
                return '#4caf50';
            case 2:
                return '#ff9800';
            case 3:
                return '#2196f3';
            case 4:
                return '#f44336';
            default:
                return '#9e9e9e';
        }
    };

    const handlePermissionChange = (value: number) => {
        setPermissions((prev) =>
            prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
        );
    };

    const handleEditingPermissionChange = (value: number) => {
        setEditingPermissions((prev) =>
            prev.includes(value) ? prev.filter((p) => p !== value) : [...prev, value]
        );
    };

    const permissionActionOptions = [
        { label: "Criar", value: 1 },
        { label: "Visualizar", value: 2 },
        { label: "Editar", value: 3 },
        { label: "Excluir", value: 4 },
    ];

    const allModules = Object.values(PermissionModule).filter(
        (v): v is PermissionModule => typeof v === "number"
    );

    const handleModulePermissionChange = (module: PermissionModule, action: number) => {
        setEditingModulePermissions(prev => {
            const existing = prev.find(mp => mp.module === module);
            if (existing) {
                const updatedPerms = existing.permissions.includes(action)
                    ? existing.permissions.filter(p => p !== action)
                    : [...existing.permissions, action];
                if (updatedPerms.length === 0) {
                    return prev.filter(mp => mp.module !== module);
                }
                return prev.map(mp => mp.module === module ? { ...mp, permissions: updatedPerms } : mp);
            }
            return [...prev, { module, permissions: [action] }];
        });
    };

    const hasPermission = createPermissionChecker(useLegacyPermissions, userPermissions, modulePermissions);

    const [editingUserType, setEditingUserType] = useState<UpdateUserTypeType | null>(null);
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const [loading, setLoading] = useState(false);

    const [currentPage, setCurrentPage] = useState(1);

    const [filters, setFilters] = useState({
        type: '',
    });

    useEffect(() => {
        setLoading(true);
        getAllUserTypePaged(1, 9, objUserTk.EmpresaId)
            .then(data => {
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao buscar tipos de usuários:", error);
                setLoading(false);
            });
    }, []);

    useEffect(() => {
        const fetchPermissions = async () => {
            if (objUserTk?.UserId) {
                const permissions = await getPermissionsByUser(objUserTk.UserId);
            }
        };

        fetchPermissions().catch(error => console.error("Erro ao carregar permissões:", error));
    }, [objUserTk?.UserId]);

    const onChangePaginationCommon = async (e: any, value: any) => {
        await getAllUserTypePaged(value, 9, objUserTk.EmpresaId);
    };

    const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSearch = () => {
        setLoading(true);
        getAllUserTypePaged(
            1,
            9,
            objUserTk.EmpresaId,
        )
            .then(() => {
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao aplicar filtros:", error);
                setLoading(false);
            });
    };

    const handleClearFilters = () => {
        getAllUserTypePaged(
            1,
            9,
            objUserTk.EmpresaId,
        )
            .then(() => {
                setLoading(false);
            })
            .catch(error => {
                console.error("Erro ao liimpar filtros:", error);
                setLoading(false);
            });
    };

    const handleCreateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewUserType(prevUserType => ({
            ...prevUserType,
            [name]: value === '' ? null : value
        }));
    };

    const handleCreateUserType = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const payload = {
            ...newUserType,
            type: newUserType.type,
            empresaId: Number(newUserType.empresaId),
            permissions
        };

        createUserType(payload)
            .then(createdUserType => {
                if (createdUserType) {
                    setCreateModalOpen(false);
                    setNewUserType({
                        type: '',
                        empresaId: objUserTk.EmpresaId,
                        canViewAllSales: false,
                        permissions: [],
                    });
                    getAllUserTypePaged(1, 9, objUserTk.EmpresaId);
                } else {
                    console.error("Erro ao criar tipo de usuário.");
                }
            })
            .catch(error => {
                console.error("Erro ao criar tipo de usuário:", error);
            });
    };

    const handleEditUserType = (userType: UserTypeType) => {
        setEditingPermissions(userType.permissions);
        setEditingModulePermissions(userType.modulePermissions ?? []);
        const updateData: UpdateUserTypeType = {
            id: userType.id,
            type: userType.type,
            canViewAllSales: userType.canViewAllSales,
            permissions: userType.permissions
        };
        setEditingUserType(updateData);
        setEditModalOpen(true);
    };

    const handleEditSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (editingUserType) {
            const updatedData = {
                ...editingUserType,
                permissions: editingPermissions
            };
            updateUserType(updatedData)
                .then(updatedUserType => {
                    if (updatedUserType) {
                        return updateModulePermissions(editingUserType.id, editingModulePermissions)
                            .then(() => {
                                setEditModalOpen(false);
                                getAllUserTypePaged(1, 9, objUserTk.EmpresaId);
                                setEditingUserType(null);
                            });
                    }
                })
                .catch(error => {
                    console.error("Erro ao atualizar o tipo da usuário:", error);
                });
        }
    };

    const handleDeleteUserType = (userTypeId: number) => {
        if (window.confirm("Tem certeza que deseja excluir este tipo de usuário?")) {
            deleteUserType(userTypeId)
                .then(deletedUserType => {
                    if (deletedUserType) {
                        getAllUserTypePaged(1, 9, objUserTk.EmpresaId);
                    } else {
                        console.error("Erro ao excluir tipo de usuário.");
                    }
                    getAllUserTypePaged(1, 9, objUserTk.EmpresaId);
                })
                .catch(error => {
                    console.error("Erro ao excluir tipo de usuário:", error);
                });
        }
    };

    return (
        <>
            <LoadingSpinner />
            <Header />
            <AdminNavbar
                brandText={"Tipos de Usuários"}
                search={false}
                titleSearch={""}
            />

            <Container className="mt-5">
                <Row className="align-items-center mb-4">
                    {hasPermission(PermissionModule.GestaoUsuarios, 1) && (
                        <Col xs="12" className="text-right mt-3">
                            <Button
                                style={{ color: "#FFF", backgroundColor: "#585858" }}
                                className="btn-filter"
                                onClick={() => setCreateModalOpen(true)}
                            >
                                Adicionar tipo de usuário
                            </Button>
                        </Col>
                    )}
                </Row>

                {hasPermission(PermissionModule.GestaoUsuarios, 2) ? (
                    <Row className="mt-4">
                        <Col>
                            <Table hover responsive className="table-striped custom-table">
                                <thead>
                                    <tr>
                                        <th>Tipo</th>
                                        <th>Permissões</th>
                                        <th>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {listUserType.map((userType: UserTypeType, index: number) => {
                                        return (
                                            <tr key={index}>
                                                <td>{userType.type}</td>
                                                <td>
                                                    {userType.permissions === null ? (
                                                        permissionOptions.map((option) => {
                                                            const permissionLabel = option.label;
                                                            const permissionColor = getPermissionColor(option.value);

                                                            return (
                                                                <div
                                                                    key={option.value}
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        padding: '5px 10px',
                                                                        borderRadius: '20px',
                                                                        backgroundColor: permissionColor,
                                                                        color: '#fff',
                                                                        marginRight: '8px',
                                                                        fontSize: '14px',
                                                                    }}
                                                                >
                                                                    {permissionLabel}
                                                                </div>
                                                            );
                                                        })
                                                    ) : userType.permissions.length > 0 ? (
                                                        userType.permissions.map((permission) => {
                                                            const permissionLabel = permissionOptions.find(option => option.value === permission)?.label;
                                                            const permissionColor = getPermissionColor(permission);

                                                            return (
                                                                <div
                                                                    key={permission}
                                                                    style={{
                                                                        display: 'inline-block',
                                                                        padding: '5px 10px',
                                                                        borderRadius: '20px',
                                                                        backgroundColor: permissionColor,
                                                                        color: '#fff',
                                                                        marginRight: '8px',
                                                                        fontSize: '14px',
                                                                    }}
                                                                >
                                                                    {permissionLabel}
                                                                </div>
                                                            );
                                                        })
                                                    ) : (
                                                        <div style={{ fontSize: '14px', color: 'gray' }}>Sem permissões</div>
                                                    )}
                                                </td>
                                                <td>
                                                    {hasPermission(PermissionModule.GestaoUsuarios, 3) && (
                                                        <Button onClick={() => { handleEditUserType(userType); setSelectedUserTypeId(userType.id); }}><i className="fa-solid fa-pen-to-square"></i></Button>
                                                    )}
                                                    {hasPermission(PermissionModule.GestaoUsuarios, 4) && (
                                                        <Button onClick={() => handleDeleteUserType(userType.id)} color="danger"><i className="fa-solid fa-trash"></i></Button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                            <div className="d-flex justify-content-end mt-3">
                                {pageCount && (
                                    <div className="d-flex justify-content-center mt-4">
                                        <PaginationMaterial
                                            onChange={(e: any, value: any) => onChangePaginationCommon(e, value)}
                                            count={pageCount}
                                        />
                                    </div>
                                )}
                                {listUserType.length === 0 && (
                                    <div style={{ color: "red" }}>NÃO FOI ENCONTRADO NENHUM REGISTRO</div>
                                )}
                            </div>
                        </Col>
                    </Row>
                ) : (
                    <Row className="mt-4">
                        <Col>
                            <div style={{ color: "red", textAlign: "center" }}>
                                Você não tem permissão para visualizar os tipos de usuário.
                            </div>
                        </Col>
                    </Row>
                )}
            </Container>

            <Modal isOpen={createModalOpen} toggle={() => setCreateModalOpen(!createModalOpen)} backdrop="static" size="lg">
                <ModalHeader toggle={() => setCreateModalOpen(!createModalOpen)}>Adicionar novo tipo de usuário</ModalHeader>
                <ModalBody>
                    <Form onSubmit={handleCreateUserType}>
                        <FormGroup>
                            <Label className="form-control-label" htmlFor="input-description">
                                Tipo de usuário:
                            </Label>
                            <Input
                                className="form-control-alternative"
                                type="text"
                                name="type"
                                id="type"
                                value={newUserType.type}
                                onChange={handleCreateInputChange}
                                required
                                placeholder="Ex: Administrador, Usuário Comum, etc..."
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label className="form-control-label" htmlFor="permissions">
                                Permissões:
                            </Label>
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                                {permissionOptions.map((option) => (
                                    <label key={option.value} style={{ display: 'flex', alignItems: 'center' }}>
                                        <input
                                            type="checkbox"
                                            value={option.value}
                                            checked={permissions.includes(option.value)}
                                            onChange={() => handlePermissionChange(option.value)}
                                            style={{ marginRight: '8px' }}
                                        />
                                        {option.label}
                                    </label>
                                ))}
                            </div>
                        </FormGroup>
                        <FormGroup>
                            <Label className="form-control-label" htmlFor="canViewAllSales">
                                Visualiza todas as vendas?
                            </Label>
                            <Input
                                type="select"
                                name="canViewAllSales"
                                id="canViewAllSales"
                                value={newUserType.canViewAllSales ? 'true' : 'false'}
                                onChange={(e) => setNewUserType({ ...newUserType, canViewAllSales: e.target.value === 'true' })}
                            >
                                <option value="false">Não</option>
                                <option value="true">Sim</option>
                            </Input>
                        </FormGroup>

                        <div className="d-flex justify-content-end mt-3">
                            <Button style={{ color: "#FFF", backgroundColor: "#585858" }} type="submit" size="m">Criar Tipo de Usuário</Button>
                            <Button style={{ color: "#FFF", backgroundColor: "#585858" }} onClick={() => setCreateModalOpen(!createModalOpen)} size="m" className="ml-2">Cancelar</Button>
                        </div>
                    </Form>
                </ModalBody>
            </Modal>

            <Modal isOpen={editModalOpen} toggle={() => setEditModalOpen(!editModalOpen)} backdrop="static" className="custom-modal-xl">
                <ModalHeader toggle={() => setEditModalOpen(!editModalOpen)}>
                    Editar informações do tipo de usuário
                </ModalHeader>
                <ModalBody>
                    {editingUserType && (
                        <>
                            <Form onSubmit={handleEditSubmit}>
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="input-description">
                                        Tipo do usuário
                                    </Label>
                                    <Input
                                        className="form-control-alternative"
                                        type="text"
                                        name="type"
                                        id="type"
                                        value={editingUserType.type}
                                        onChange={e => setEditingUserType({ ...editingUserType, type: e.target.value })}
                                        required
                                    />
                                </FormGroup>
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="permissions">
                                        Permissões:
                                    </Label>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
                                        {permissionOptions.map(option => (
                                            <label key={option.value} style={{ display: 'flex', alignItems: 'center' }}>
                                                <input
                                                    type="checkbox"
                                                    checked={editingPermissions ? editingPermissions.includes(option.value) : true}
                                                    onChange={() => handleEditingPermissionChange(option.value)}
                                                    style={{ marginRight: '8px' }}
                                                />
                                                {option.label}
                                            </label>
                                        ))}
                                    </div>
                                </FormGroup>
                                <FormGroup>
                                    <Label className="form-control-label" htmlFor="canViewAllSales">
                                        Visualiza todas as vendas?
                                    </Label>
                                    <Input
                                        type="select"
                                        name="canViewAllSales"
                                        id="canViewAllSales"
                                        value={editingUserType.canViewAllSales ? 'true' : 'false'}
                                        onChange={e => setEditingUserType({ ...editingUserType, canViewAllSales: e.target.value === 'true' })}
                                    >
                                        <option value="false">Não</option>
                                        <option value="true">Sim</option>
                                    </Input>
                                </FormGroup>
                                <FormGroup>
                                    <Label className="form-control-label">
                                        Permissões por Módulo:
                                    </Label>
                                    <div className="permissions-table-container">
                                        <table className="permissions-table">
                                            <thead>
                                                <tr>
                                                    <th></th>
                                                    {permissionActionOptions.map(opt => (
                                                        <th key={opt.value}>{opt.label}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allModules.map((mod, idx) => {
                                                    const modPerms = editingModulePermissions.find(mp => mp.module === mod)?.permissions ?? [];
                                                    return (
                                                        <tr key={mod}>
                                                            <td className="module-name">{MODULE_LABELS[mod]}</td>
                                                            {permissionActionOptions.map(opt => (
                                                                <td key={opt.value} className="permission-cell">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={modPerms.includes(opt.value)}
                                                                        onChange={() => handleModulePermissionChange(mod, opt.value)}
                                                                    />
                                                                </td>
                                                            ))}
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                </FormGroup>
                                <div className="d-flex justify-content-end">
                                    <Button style={{ color: "#FFF", backgroundColor: "#585858" }} type="submit" size="m">Salvar alterações</Button>
                                    <Button style={{ color: "#FFF", backgroundColor: "#585858" }} onClick={() => setEditModalOpen(!editModalOpen)} size="m" className="ml-2">Cancelar</Button>
                                </div>
                            </Form>
                        </>
                    )}
                </ModalBody>
            </Modal>
        </>
    );
};

export default UserType;
